<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
  v-card
    v-card-text.pt-8.pb-0.primary--text
      .d-flex.align-center.mb-6
        v-btn.mr-4(icon small)
          v-icon(color="primary" @click="back") mdi-arrow-left
        .text-2xl.font-semibold {{ type | capitalize }} ranking

      v-row.pb-2.pb-md-0(no-gutters)
        v-col(cols="12" md="6")
          v-tabs.mb-2.mb-md-0(color="basic700" height="44")
            v-tab.text-sm.font-normal.text-none(active-class="font-semibold") All

        v-col(cols="12" md="6" align="end")
          .d-flex.align-center
            v-spacer
            v-btn(icon color="neutral500" @click="onClickSearch")
              v-icon(size="20") $yzi-search-sm
            v-text-field(v-if="showSearchField" v-model="searchText" @blur="onBlurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
      v-divider.mb-6

      .data-table
        v-data-table(:headers="headers" :items="items" hide-default-footer :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2" :loading="isLoading" :items-per-page="pageSize")
          template(v-slot:item.sales_amount="{ item }")
            div {{ item.sales_amount | displayPriceNoCent }}
          template(v-slot:item.provider="{ item }")
            div {{ item.first_name }} {{ item.last_name }}
          template(v-slot:item.leads_count="{ item }")
            .text-right {{ item.leads_count }}
          template(v-slot:item.unit_sold="{ item }")
            .text-right {{ item.unit_sold }}
          template(v-slot:item.billed_amount="{ item }")
            div {{ item.billed_amount | displayPrice }}
          template(v-slot:item.rejected_amount="{ item }")
            div {{ item.rejected_amount | displayPrice }} ({{ item.rejected_pct | toDecimalPlaces(1) }}%)
          template(v-slot:item.revenue_amount="{ item }")
            div {{ item.revenue_amount | displayPrice }}
          template(v-slot:item.due_amount="{ item }")
            div {{ item.due_amount | displayPrice }}
          template(v-slot:item.unpaid_amount="{ item }")
            div {{ item.unpaid_amount | displayPrice }}

        .d-flex.align-center(v-if="totalCount > pageSize")
          v-spacer
          yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" @input="pageChanged")

    v-card-actions.white.pa-0.ma-0
      .d-flex.flex-column.grow
        v-divider
        .d-flex.justify-end.align-center.my-4.mx-8
          v-btn.button-sm(depressed color="primary" @click="back") {{ $t('done') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'

export default {
  name: 'RankingTableDialog',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    providers: {
      type: Array,
    },
    begin: {
      type: String
    },
    until: {
      type: String
    },
  },
  data: () => ({
    isLoading: false,
    showSearchField: false,
    searchText: null,
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    serviceHeaders: [
      { text: '#', value: 'id' },
      { text: 'Services', value: 'name' },
      { text: 'Sales', value: 'sales_amount'  },
      { text: 'Patients', value: 'leads_count' },
    ],
    items: [],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      selectedCompanies: 'insight/getSelectedCompanies',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    headers () {
      switch (this.type) {
        case 'services':
          return [
            { text: '#', value: 'index' },
            { text: 'Services', value: 'name' },
            { text: 'Sales', value: 'sales_amount'  },
            { text: 'Patients', value: 'leads_count' },
          ]
        case 'medication':
          return [
            { text: '#', value: 'index' },
            { text: 'Medication', value: 'name' },
            { text: 'Sales', value: 'sales_amount'  },
            { text: 'Unit Sold', value: 'unit_sold' },
          ]
        case 'packages':
          return [
            { text: '#', value: 'index' },
            { text: 'Packages', value: 'name' },
            { text: 'Sales', value: 'sales_amount'  },
            { text: 'Unit Sold', value: 'unit_sold' },
          ]
        case 'provider':
          return [
            { text: '#', value: 'index' },
            { text: 'Doctor', value: 'provider' },
            { text: 'Sales', value: 'sales_amount'  },
            { text: 'Patients', value: 'leads_count' },
          ]
        case 'branch':
          return [
            { text: '#', value: 'index' },
            { text: 'Branch', value: 'branch' },
            { text: 'Sales', value: 'sales_amount'  },
            { text: 'Patients', value: 'leads_count' },
          ]
        case 'panel':
          return [
            { text: '#', value: 'index' },
            { text: 'Panel Name', value: 'name' },
            { text: 'Billed', value: 'billed_amount' },
            { text: 'Rejected', value: 'rejected_amount' },
            { text: 'Revenue', value: 'revenue_amount' },
            { text: 'Due', value: 'due_amount' },
          ]
        case 'outstanding':
          return [
            { text: 'Name', value: 'name' },
            { text: 'Sales', value: 'unpaid_amount' },
          ]
        default:
          return []
      }
    },
    rankingEndpoint () {
      switch (this.type) {
        case 'services':
          return 'sales/services'
        case 'medication':
          return 'sales/medications'
        case 'packages':
          return 'sales/packages'
        case 'provider':
          return 'sales/providers'
        case 'panel':
          return 'payments/panels'
        case 'outstanding':
          return 'invoices'
        default:
          return ''
      }
    },
  },
  watch: {
    pageSize () {
      this.currentPage = 1
      this.fetchData()
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchData (stopRefetch) {
      this.isLoading = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage,
        form_type: 'registration',
        begin: this.begin,
        until: this.until
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.providers) {
        params.providers = this.providers.map(p => p.id).join(',')
      }
      if (this.searchText) {
        params.search = this.searchText
      }
      this.$axios.get(`/rankings/${this.company.slug}/${this.rankingEndpoint}`, { params })
        .then(resp => {
          this.items = resp.data.results
          this.items.forEach((row, index) => {
            row.index = (index + 1) + (this.pageSize * (this.currentPage - 1))
          })
          this.totalCount = resp.data.count
          this.isLoading = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchData(true)
          } else {
            this.isLoading = false
          }
        })
    },
    back () {
      this.showDialog = false
    },
    onClickSearch() {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    onBlurSearch() {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.fetchData()
    }, 750),
    pageChanged (num) {
      this.currentPage = num
      this.fetchData()
    },
  },
}
</script>